<template>
  <div v-if="chartingData">
    <div v-if="usesCorrectedViscosityRate" class="flex justify-content-end">
      <div class="flex text-xs p-1">* Uses corrected viscosity rate</div>
    </div>
    <PumpTornadoHeadChart
      :key="headTornadoKey"
      :aspect-ratio="2"
      draw-legend
      is-horizontal
      is-static
      show-pump-table
      :title="scenario?.title || ''"
      :show-operating-frequency="false"
      :min-frequency="frequencyRange[0]"
      :max-frequency="frequencyRange[1]"
      x-legend-label="In Situ Flow Rate"
      y-legend-label="TDH"
      :scenario="props.scenario"
      :scenario-calculations="props.scenarioCalculations"
      :pump-tornado-chart-data="chartingData"
    />
    <PumpStageTable
      :descending-order-pumps="scenarioCalculations.descendingOrderPumps.value"
      :specific-gravity="
        Math.round(
          convert(
            scenarioCalculations.pumpCalculations.aggregate
              .averageLiquidDensityLBMFT3.value,
            DensityUnits.PoundsPerCubicFoot,
            DensityUnits.SpecificGravity
          ) * 100
        ) / 100
      "
    />

    <section class="break-inside-avoid">
      <h3>
        Head Curve Analysis
        {{ scenario?.title }}
      </h3>
      <StartUpHeadReport
        v-if="scenario"
        :min-frequency="minFrequency"
        :max-frequency="maxFrequency"
        :scenario="scenario"
        :scenario-calculations="scenarioCalculations"
        :pump-tornado-chart-data="chartingData"
      />
    </section>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import PumpTornadoHeadChart from "./PumpTornadoHeadChart.vue";
import PumpStageTable from "./PumpStageTable.vue";
import StartUpHeadReport from "./StartUpHeadReport.vue";
import { usePumpChartingData } from "@/libs/charting";
import { useDefaultPumpFrequencyRange } from "@/composables/useDefaultPumpFrequencyRange";
import type { ScenarioCalculations } from "@/composables/scenarioCalculations/calculationTypes";
import type { Scenario } from "@/composables/forms/formTypes";
import { v4 } from "uuid";
import { DensityUnits } from "@/libs/units/density";
import { convert } from "@/libs/units";
import { sizingKey } from "@/composables/scenarioDetailProvider/scenarioDetailProviderKeys";
import { useScenarioUnits } from "@/composables/units/useScenarioUnits";
import { injectStrict } from "@/utils/utils";

const props = defineProps<{
  chartMinHeight: number;
  minFrequency: number;
  maxFrequency: number;
  scenario?: Scenario | null;
  scenarioCalculations: ScenarioCalculations;
}>();
/**
 * Since the tornado charts are not reactive by default, we must setup
 * a watcher to re-render them when the frequencyRange is updated
 * MAYBE TODO: review with lou
 */
const headTornadoKey = ref(v4());

// const { pumpHz, primaryPump } = injectStrict(scenarioCalculationsKey);
const { pumpHz, primaryPump } = props.scenarioCalculations;

const { frequencyRange } = useDefaultPumpFrequencyRange(pumpHz, {
  reactiveUpdates: false
});

const usesCorrectedViscosityRate = computed(
  () =>
    primaryPump.value?.rate_correction_ratio !== 0 &&
    primaryPump.value?.rate_correction_ratio !== 1
);

const _sizing = injectStrict(sizingKey, null);

const {
  getLiquidFlowUnit,
  getHydraulicPowerUnit,
  getLargeLengthUnit,
  getPressureUnit
} = useScenarioUnits(props.scenario, _sizing);

const targetLiquidFlowUnit = getLiquidFlowUnit(
  "Target Liquid Rate.write.STB/D"
);
const targetPowerUnit = getHydraulicPowerUnit("Shaft Limit.convert.hp");
const targetLargeLengthUnit = getLargeLengthUnit("Required TDH.convert.Feet");
const targetPressureUnit = getPressureUnit(
  "Producing Bottomhole Pressure at Datum [Pwf].write.PSIA"
);

const chartingData = computed(() =>
  usePumpChartingData(
    props.minFrequency,
    props.maxFrequency,
    props.scenarioCalculations,
    targetLiquidFlowUnit.value,
    targetPowerUnit.value,
    targetLargeLengthUnit.value,
    targetPressureUnit.value
  )
);
</script>

<style lang="scss" scoped>
.top {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 0.5em;
}
.two-up {
  display: grid;
  gap: 1em;
  grid-template-columns: repeat(auto-fit, minmax(360px, 1fr));
}
.chart-grid {
  padding-bottom: 1em;
  display: grid;
  gap: 1em;
  grid-template-columns: repeat(auto-fit, minmax(360px, 1fr));

  > div {
    box-shadow: var(--shadow-main);
    padding: 1.5em 1.5em 0em 1em;
    background-color: var(--surface-300);
    border-radius: 0.25em;
  }
}
</style>
